import { useNavigate } from '@tanstack/react-router'
import pluralize from 'pluralize'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  TextField,
} from '@/components/ui'
import { useToast } from '@/hooks'
import {
  useFilingMutations,
  useFilingsMutating,
  useScreeningLimits,
} from '@/modules/tools/bankruptcy/hooks'
import { useDialogActions, useDialogIsOpen, useForm } from '@/stores'
import { formatNumber, getFilingPath } from '@/utils'
import { NeedMore } from './NeedMore'

export const NEW_CASE_DIALOG_ID = 'new-bankruptcy-case'

export const NewCaseDialog = () => {
  const open = useDialogIsOpen(NEW_CASE_DIALOG_ID)
  const { closeDialog } = useDialogActions()
  const { data, errors, setFieldValue, setFieldError } = useForm()
  const { createFiling } = useFilingMutations()
  const creating = useFilingsMutating()
  const limits = useScreeningLimits()
  const depleted = limits?.outOfCredits ?? false
  const { successToast, errorToast } = useToast()
  const navigate = useNavigate()

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (depleted) return

    const name = ((data?.name as string) ?? '').trim()

    if (name.length === 0) {
      setFieldError('name', 'Name is required')
      return
    }

    createFiling({
      name,
      case_number: (data?.caseNumber as string) || undefined,
    })
      .then((filing) => {
        closeDialog(true)
        successToast('Case created')
        navigate({ to: getFilingPath(filing) })
      })
      .catch((error) => errorToast('Failed to create case', error))
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && closeDialog()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New case</DialogTitle>
          <DialogDescription>
            Name the case. The case number is optional and always yours to
            change.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 py-2">
          <TextField
            label="Case name"
            id="case-name"
            placeholder="e.g. Hernandez"
            value={(data?.name as string) ?? ''}
            error={errors?.name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFieldValue('name', event.target.value)
            }
            required
            autoFocus
          />
          <TextField
            label="Case number (optional)"
            id="case-number"
            placeholder="e.g. 24-12345"
            value={(data?.caseNumber as string) ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFieldValue('caseNumber', event.target.value)
            }
          />
          {limits && (
            <p
              className={
                depleted
                  ? 'text-amber-600 text-sm'
                  : 'text-muted-foreground text-sm'
              }
            >
              {depleted
                ? limits.creditsNextExpiry
                  ? `You're out of screening credits. Your allowance renews ${limits.creditsNextExpiry}.`
                  : "You're out of screening credits for now."
                : `${formatNumber(limits.credits)} screening ${pluralize('credit', limits.credits)} in your allowance.`}{' '}
              {depleted && <NeedMore limit="allowance" />}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" theme="outline" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button type="submit" isLoading={creating} disabled={depleted}>
              Create case
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
